import React, { useEffect, useState } from "react";
import { observer, inject } from "mobx-react";
import LoadingContainer from "../layouts/LoadingContainer";
import { subMonths, getMonth, getYear } from "date-fns";
import { useMobile } from "../utils/mobile";
import MonthReports from "./MonthReports";
import MonthReportsMobile from "./MonthReportsMobile";

const MonthReportsMain = ({ userStore, monthReportsStore }) => {
  const isMobile = useMobile();
  const [isLoading, setLoading] = useState(false);
  const [gls, setGls] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [monthReports, setMonthReports] = useState([]);
  const [date, setDate] = useState(subMonths(new Date(), 1));
  const [error, setError] = useState("");
  const [selectedExp, setSelectedExp] = useState(undefined);
  const [period, setPeriod] = useState(null);
  const [gl, setGL] = useState(null);

  const keys = [
    { key: "CURDATE", width: "15%" },
    { key: "DAYNAME", width: "5%" },
    { key: "STANDARD", width: "5%" },
    { key: "ILL", width: "5%" },
    { key: "SERV", width: "5%" },
    { key: "VAC", width: "5%" },
    { key: "HOURS", width: "10%" },
    { key: "EXTRA", width: "5%" },
    { key: "HOLIDAYDES", width: "10%" },
    { key: "DES", width: "10%" },
    { key: "EXTFILEDES", width: "15%", link: true },
    { key: "ACTIONS", width: "10%" },
  ];

  const expKeys = [
    { key: "EXTFILEDES", width: "10%" },
    { key: "FILENAMEDES", width: "70%", link: true },
    { key: "VATPRICE", width: "10%" },
    { key: "ACTIONS", width: "10%" },
  ];

  const monthReportsKeys = [
    { key: "MONTHLYHOURS", width: "10%" },
    { key: "REPORTEDHOURS", width: "15%" },
    { key: "ILLHOURS", width: "10%" },
    { key: "VACHOURS", width: "10%" },
    { key: "SERVHOURS", width: "10%" },
    { key: "EXTRAHOURS", width: "10%" },
    { key: "MISSHOURS", width: "10%" },
    { key: "TOTAL", width: "10%" },
    { key: "ACTIONS", width: "10%" },
  ];

  const modifyMonthReportData = (monthReport) => {
    const totalHours = !!monthReport
      ? (
          (monthReport?.REPORTEDHOURS ?? 0) +
          (monthReport?.ILLHOURS ?? 0) +
          (monthReport?.VACHOURS ?? 0) +
          (monthReport?.SERVHOURS ?? 0) +
          Math.abs(monthReport?.EXTRA ?? 0)
        ).toFixed(2)
      : "0";

    const displayTotalHours = totalHours === "0.00" ? "0" : totalHours;
    const modifiedMonthReport = {
      ...monthReport,
      EXTRAHOURS: monthReport.EXTRA > 0 ? monthReport.EXTRA : 0,
      MISSHOURS: monthReport.EXTRA < 0 ? Math.abs(monthReport.EXTRA) : 0,
      TOTAL: displayTotalHours,
    };
    return modifiedMonthReport;
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      monthReportsStore.setError("");
      await monthReportsStore.getMonthReportsStatuses();
      const gls = await monthReportsStore.getGls();

      if (!monthReportsStore.isError) {
        setGls(gls);
        setStatuses(monthReportsStore.monthReportsStatuses);
      }
      setLoading(false);
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const monthReport = modifyMonthReportData(monthReportsStore.monthReports);
    setMonthReports(monthReport);
  }, [monthReportsStore.monthReports]);

  useEffect(() => {
    const fetchMonthReports = async () => {
      setLoading(true);
      monthReportsStore.setError("");

      const period = getMonth(date) + 1;
      const year = getYear(date);
      setPeriod(period);

      const gl = gls.find((gl) => gl.GLNAME === year.toString())?.GL;
      setGL(gl);
      if (!!gl) {
        const data = {
          ZCR_USERID: userStore.userDetails.ZCR_USERB,
          PERIOD: period,
          GL: gl,
        };

        await monthReportsStore.getMonthReports(data);

        const monthReport = modifyMonthReportData(
          monthReportsStore.monthReports
        );

        setMonthReports(monthReport);
      }

      setLoading(false);
    };
    fetchMonthReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, gls]);

  const handelWorkHoursCalc = async () => {
    setLoading(true);
    setError("");
    const body = {
      PERIOD: period,
      GL: gl,
      USERID: userStore.userDetails.ZCR_USERB,
    };
    const selectedMonthReport =
      monthReportsStore.monthReports.GL === gl &&
      monthReportsStore.monthReports.PERIOD === period;

    if (!selectedMonthReport) {
      await monthReportsStore.addReport({
        ...body,
        USERID: userStore.userDetails.ZCR_USERID,
      });
    }

    await monthReportsStore.updateReports({
      ...body,
      USERID: userStore.userDetails.ZCR_USERB,
      ZCR_USERID: userStore.userDetails.ZCR_USERB,
      data: { ZCOP_MONTHCALC: "Y" },
    });
    setLoading(false);

    if (!!monthReportsStore.error) {
      setError(monthReportsStore.error);
    }
  };

  const handelRowDeleteClick = async (e, report) => {
    const data = {
      EXTFILENUM: report.EXTFILENUM,
      ZCR_USERID: userStore.userDetails.ZCR_USERB,
      PERIOD: period,
      GL: gl,
    };
    e.stopPropagation();
    setLoading(true);
    await monthReportsStore.deleteExpense(data);

    if (!monthReportsStore.error) {
      const monthReport = modifyMonthReportData(monthReportsStore.monthReports);

      setMonthReports(monthReport);
    }

    setLoading(false);
  };

  const handleDateChange = (date) => {
    setDate(date);
  };

  const handleStatusChange = async (status) => {
    setError("");
    const data = {
      STATDES: status.label,
    };

    const body = {
      PERIOD: period,
      GL: gl,
      ZCR_USERID: userStore.userDetails.ZCR_USERB,
      data: data,
    };
    setLoading(true);
    await monthReportsStore.updateReports(body);
    setLoading(false);
    if (!monthReportsStore.error) {
      const monthReport = modifyMonthReportData(monthReportsStore.monthReports);

      setMonthReports(monthReport);
    } else {
      setError(monthReportsStore.error);
    }
  };

  const onSaveExpense = async (data) => {
    selectedExp !== undefined
      ? await monthReportsStore.updateExpense(data)
      : await monthReportsStore.addExpense(data);

    if (!monthReportsStore.error) {
      const monthReport = modifyMonthReportData(monthReportsStore.monthReports);

      setMonthReports(monthReport);
    }
  };

  const onSaveReport = async (data) => {
    await monthReportsStore.updateReport(data);

    if (!monthReportsStore.error) {
      const monthReport = modifyMonthReportData(monthReportsStore.monthReports);

      setMonthReports(monthReport);
    }
  };

  return (
    <LoadingContainer isLoading={isLoading}>
      {isMobile ? (
        <MonthReportsMobile
          keys={keys}
          expKeys={expKeys}
          monthReportsKeys={monthReportsKeys}
          monthReports={monthReports}
          handelWorkHoursCalc={handelWorkHoursCalc}
          handelRowDeleteClick={handelRowDeleteClick}
          handleDateChange={handleDateChange}
          handleStatusChange={handleStatusChange}
          onSaveExpense={onSaveExpense}
          onSaveReport={onSaveReport}
          date={date}
          statuses={statuses}
          gl={gl}
          period={period}
          error={error}
        />
      ) : (
        <MonthReports
          keys={keys}
          expKeys={expKeys}
          monthReportsKeys={monthReportsKeys}
          monthReports={monthReports}
          handelWorkHoursCalc={handelWorkHoursCalc}
          handelRowDeleteClick={handelRowDeleteClick}
          handleDateChange={handleDateChange}
          handleStatusChange={handleStatusChange}
          onSaveExpense={onSaveExpense}
          onSaveReport={onSaveReport}
          date={date}
          statuses={statuses}
          gl={gl}
          period={period}
          error={error}
        />
      )}
    </LoadingContainer>
  );
};

export default inject(
  "userStore",
  "monthReportsStore"
)(observer(MonthReportsMain));
